import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  CircleAlert,
  Cloud,
  CloudUpload,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import {
  useCallSummary,
  useGenerateCallSummary,
  usePushCallToCrm,
} from "@/features/calls/hooks";
import { integrationsApi, isCrmIntegration } from "@/features/integrations/api";
import type { Integration } from "@/features/integrations/api";
import { cn } from "@/lib/utils";

const SENTIMENT_STYLES: Record<string, string> = {
  positive:
    "text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
  neutral: "text-slate-700 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
  negative: "text-rose-700 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400",
};

const INTEGRATION_LABELS: Record<string, string> = {
  hubspot: "HubSpot",
  salesforce: "Salesforce",
  custom: "Webhook",
};

export default function CallSummaryCard({
  callId,
  hasTranscript,
}: {
  callId: string;
  hasTranscript: boolean;
}) {
  const role = useAuthStore((s) => s.user?.role) ?? "member";
  const canEdit = hasMinRole(role, "manager");

  const sumQ = useCallSummary(callId);
  const gen = useGenerateCallSummary(callId);
  const push = usePushCallToCrm(callId);

  const integrationsQ = useQuery({
    queryKey: ["integrations", "list"],
    queryFn: integrationsApi.list,
    staleTime: 60_000,
  });

  const crmIntegrations: Integration[] =
    (integrationsQ.data ?? []).filter(
      (i) => isCrmIntegration(i.integration_type) && i.has_credentials,
    ) || [];

  const [pushingId, setPushingId] = useState<string | null>(null);

  if (sumQ.isLoading) return <Skeleton className="h-40 w-full" />;
  if (sumQ.isError || !sumQ.data) return null;

  const sum = sumQ.data;
  const s = sum.structured_summary || {};
  // "completed" status OR an existing structured summary means the card has
  // content to render. Old calls default to status='pending' but have no
  // summary content yet — we treat that as "not generated."
  const hasContent =
    sum.summary_status === "completed" ||
    !!sum.structured_summary?.summary ||
    !!sum.summary;
  const summaryReady = hasContent;
  // "processing" is the only in-flight state. "pending" means a task hasn't
  // been kicked off yet (default value on old rows).
  const summaryProcessing =
    sum.summary_status === "processing" || gen.isPending;

  async function onGenerate() {
    try {
      await gen.mutateAsync();
      toast.info("Generating summary — this takes a few seconds.");
    } catch {
      toast.error("Failed to start summary generation");
    }
  }

  async function onPush(integrationId: string) {
    setPushingId(integrationId);
    try {
      await push.mutateAsync(integrationId);
      toast.info("Pushing to CRM…");
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      toast.error(err?.response?.data?.error || "Failed to push to CRM");
    } finally {
      setPushingId(null);
    }
  }

  // Empty state — no summary generated yet
  if (!summaryReady && !summaryProcessing) {
    if (!hasTranscript) return null;
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Summary</CardTitle>
          <CardDescription>
            Generate an AI summary of this call to extract intent, outcome,
            next steps, and key facts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {canEdit ? (
            <Button onClick={onGenerate} disabled={gen.isPending}>
              <Sparkles className="size-4" />
              {gen.isPending ? "Starting…" : "Generate summary"}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              Ask a manager to generate the summary.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="size-4 text-primary" />
              Summary
              {summaryProcessing && (
                <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                  <Loader2 className="size-3 animate-spin" />
                  Generating…
                </span>
              )}
            </CardTitle>
            <CardDescription>
              AI-extracted intent, outcome, and follow-ups.
            </CardDescription>
          </div>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onGenerate}
                disabled={gen.isPending || summaryProcessing}
              >
                <RefreshCw className="size-3.5" />
                Regenerate
              </Button>

              <PushToCrmButton
                crmIntegrations={crmIntegrations}
                disabled={!summaryReady || push.isPending}
                onPick={onPush}
                pushing={!!pushingId || push.isPending}
                pushStatus={sum.crm_push_status}
                pushTarget={sum.crm_push_target}
              />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Chips: intent / outcome / sentiment / follow-up flag */}
        <div className="flex flex-wrap items-center gap-2">
          {s.intent && (
            <Chip label="Intent" value={s.intent} variant="default" />
          )}
          {s.outcome && (
            <Chip label="Outcome" value={s.outcome} variant="default" />
          )}
          {sum.sentiment && (
            <Chip
              label="Sentiment"
              value={sum.sentiment}
              className={SENTIMENT_STYLES[sum.sentiment] || ""}
            />
          )}
          {s.caller_name && (
            <Chip label="Caller" value={s.caller_name} variant="default" />
          )}
          {s.follow_up_required && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
              <CircleAlert className="size-3" />
              Follow-up required
            </span>
          )}
        </div>

        {/* Prose summary */}
        {sum.summary && (
          <p className="text-sm leading-relaxed">{sum.summary}</p>
        )}

        {/* Next steps */}
        {s.next_steps && s.next_steps.length > 0 && (
          <div>
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Next steps
            </h4>
            <ul className="list-disc space-y-0.5 pl-5 text-sm">
              {s.next_steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Key facts */}
        {s.key_facts && s.key_facts.length > 0 && (
          <div>
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Key facts
            </h4>
            <ul className="list-disc space-y-0.5 pl-5 text-sm">
              {s.key_facts.map((fact, i) => (
                <li key={i}>{fact}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Tags */}
        {s.tags && s.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {s.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* CRM push status */}
        <CrmPushStatus
          status={sum.crm_push_status}
          target={sum.crm_push_target}
          at={sum.crm_pushed_at}
          error={sum.crm_push_error}
        />
      </CardContent>
    </Card>
  );
}

function Chip({
  label,
  value,
  variant = "default",
  className = "",
}: {
  label: string;
  value: string;
  variant?: "default";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs",
        variant === "default" && "text-foreground",
        className,
      )}
    >
      <span className="mr-1 font-medium text-muted-foreground">{label}:</span>
      {value}
    </span>
  );
}

function PushToCrmButton({
  crmIntegrations,
  disabled,
  onPick,
  pushing,
  pushStatus,
  pushTarget,
}: {
  crmIntegrations: Integration[];
  disabled: boolean;
  onPick: (id: string) => void;
  pushing: boolean;
  pushStatus: string;
  pushTarget: string;
}) {
  if (crmIntegrations.length === 0) {
    return (
      <Button variant="outline" size="sm" disabled title="No CRM connected">
        <CloudUpload className="size-3.5" />
        Push to CRM
      </Button>
    );
  }

  // If we have exactly one CRM, push directly.
  if (crmIntegrations.length === 1) {
    const i = crmIntegrations[0];
    return (
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => onPick(i.id)}
      >
        {pushing ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <CloudUpload className="size-3.5" />
        )}
        Push to {INTEGRATION_LABELS[i.integration_type] || i.integration_type}
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          {pushing ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <CloudUpload className="size-3.5" />
          )}
          Push to CRM
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {crmIntegrations.map((i) => (
          <DropdownMenuItem key={i.id} onClick={() => onPick(i.id)}>
            <Cloud className="size-3.5" />
            {INTEGRATION_LABELS[i.integration_type] || i.integration_type}
            {pushStatus === "pending" && pushTarget === i.integration_type && (
              <Loader2 className="ml-auto size-3.5 animate-spin" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CrmPushStatus({
  status,
  target,
  at,
  error,
}: {
  status: string;
  target: string;
  at: string | null;
  error: string;
}) {
  if (!status) return null;
  const label = INTEGRATION_LABELS[target] || target || "CRM";

  if (status === "success") {
    return (
      <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
        <CheckCircle2 className="size-4" />
        <span>
          Pushed to <strong>{label}</strong>
          {at && ` · ${new Date(at).toLocaleString()}`}
        </span>
      </div>
    );
  }
  if (status === "pending") {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        Pushing to {label}…
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
        <CircleAlert className="size-4 shrink-0" />
        <div>
          <div>
            Push to <strong>{label}</strong> failed
          </div>
          {error && (
            <div className="mt-0.5 text-xs opacity-80">{error.slice(0, 240)}</div>
          )}
        </div>
      </div>
    );
  }
  return null;
}
